// Save forecast dialog - allows users to save forecast workspace

import React, { useState } from 'react';
import { BookmarkIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { Modal, ModalHeader, ModalContent, ModalFooter, SuccessButton, SecondaryButton } from '@/components/ui';

interface SaveForecastDialogProps {
  isOpen: boolean;
  forecastName: string;
  onNameChange: (name: string) => void;
  onClose: () => void;
  onSave: () => void;
  isLoading: boolean;
}

export const SaveForecastDialog: React.FC<SaveForecastDialogProps> = ({
  isOpen,
  forecastName,
  onNameChange,
  onClose,
  onSave,
  isLoading
}) => {
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!forecastName.trim()) {
      newErrors.name = 'Forecast name is required';
    } else if (forecastName.trim().length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    } else if (forecastName.trim().length > 100) {
      newErrors.name = 'Name must be less than 100 characters';
    }

    if (description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSave();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const parsedTags = tags
    .split(',')
    .map(tag => tag.trim())
    .filter(tag => tag.length > 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} variant="form" size="md" backdrop="blur">
      <ModalHeader
        icon={<BookmarkIcon className="w-5 h-5 text-green-600" />}
        title="Save Forecast"
        onClose={onClose}
      />

      <ModalContent>
        <div className="space-y-4" onKeyDown={handleKeyPress}>

          {/* Forecast Name */}
          <div>
            <label htmlFor="forecast-name" className="block text-sm font-medium text-gray-700 mb-1">
              Forecast Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="forecast-name"
              value={forecastName}
              onChange={(e) => onNameChange(e.target.value)}
              disabled={isLoading}
              className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500'
                } disabled:bg-gray-100 disabled:cursor-not-allowed`}
              placeholder="Enter forecast name..."
              maxLength={100}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="w-4 h-4 mr-1" />
                {errors.name}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {forecastName.length}/100 characters
            </p>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="forecast-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description (Optional)
            </label>
            <textarea
              id="forecast-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isLoading}
              rows={3}
              className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${errors.description ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500'
                } disabled:bg-gray-100 disabled:cursor-not-allowed`}
              placeholder="Describe this forecast..."
              maxLength={500}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="w-4 h-4 mr-1" />
                {errors.description}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {description.length}/500 characters
            </p>
          </div>

          {/* Tags */}
          <div>
            <label htmlFor="forecast-tags" className="block text-sm font-medium text-gray-700 mb-1">
              Tags (Optional)
            </label>
            <input
              type="text"
              id="forecast-tags"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              disabled={isLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="forecast, maintenance, planning (comma-separated)"
            />
            <p className="mt-1 text-xs text-gray-500">
              Separate tags with commas. {parsedTags.length > 0 && `${parsedTags.length} tag${parsedTags.length !== 1 ? 's' : ''} will be added.`}
            </p>
            {parsedTags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {parsedTags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </ModalContent>

      <ModalFooter justify="end">
        <div className="flex items-center space-x-3">
          <SecondaryButton
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </SecondaryButton>

          <SuccessButton
            onClick={handleSave}
            disabled={isLoading || !forecastName.trim()}
            loading={isLoading}
            icon={!isLoading ? <BookmarkIcon className="w-4 h-4" /> : undefined}
          >
            {isLoading ? 'Saving...' : 'Save Forecast'}
          </SuccessButton>
        </div>
      </ModalFooter>
    </Modal>
  );
};
